import { DateTimeRangeOption } from '@/models/common.models';
import dayjs from '@/utils/dayjs';
import dateTimeFormats from '@/i18n/date-time-format';
import type { Dayjs } from 'dayjs';

export interface RangeOptions {
  from: string;
  to: string;
}

export function addDays(startDate: string | Date, amount: number) {
  const date = new Date(startDate);
  date.setDate(date.getDate() + amount);
  return date;
}

export function getDatesBetween(
  startDate: string | Date,
  stopDate: string | Date
) {
  const start = dayjs(startDate).toDate();
  const end = dayjs(stopDate).toDate();

  const dateArray = [];
  let current = start;
  while (current <= end) {
    dateArray.push(current);
    current = addDays(current, 1);
  }

  return dateArray;
}

export const toServerDateTime = (dt?: Date | string) => {
  if (!dt) return dt;
  const dateObj = typeof dt === 'string' ? new Date(dt) : dt;
  return dateObj.toISOString();
};

export const toServerDate = (dt: Dayjs | Date | string): string => {
  if (!dt) return dt;
  return dayjs.isDayjs(dt)
    ? dt.format('YYYY-MM-DD')
    : dayjs(dt).format('YYYY-MM-DD');
};

export function convertTo24Hour(hour: string | number, meridian: 'AM' | 'PM') {
  // Convert hour from string to number if necessary
  let hourNumeric = typeof hour === 'string' ? parseInt(hour, 10) : hour;

  if (meridian === 'PM' && hourNumeric < 12) {
    hourNumeric += 12;
  } else if (meridian === 'AM' && hourNumeric === 12) {
    hourNumeric = 0;
  }

  return hourNumeric;
}

//-------------------
// Date range helpers
//-------------------

export const standardOptions: { [key: string]: RangeOptions } = {
  Today: {
    from: toPickerFormat(dayjs().toDate()),
    to: toPickerFormat(dayjs().toDate()),
  },
  Yesterday: {
    from: toPickerFormat(dayjs().subtract(1, 'day').toDate()),
    to: toPickerFormat(dayjs().subtract(1, 'day').toDate()),
  },
  'This week': {
    from: toPickerFormat(dayjs().weekday(0).toDate()),
    to: toPickerFormat(toPickerFormat(dayjs().toDate())),
  },
  'Last week': {
    from: toPickerFormat(dayjs().weekday(-7).toDate()),
    to: toPickerFormat(dayjs().weekday(-1).toDate()),
  },
  'This month': {
    from: toPickerFormat(dayjs().startOf('month').toDate()),
    to: toPickerFormat(dayjs().toDate()),
  },
  'Last month': {
    from: toPickerFormat(
      dayjs().startOf('month').subtract(1, 'day').startOf('month').toDate()
    ),
    to: toPickerFormat(
      dayjs().startOf('month').subtract(1, 'day').endOf('month').toDate()
    ),
  },
  'This year': {
    from: toPickerFormat(dayjs().startOf('year').toDate()),
    to: toPickerFormat(dayjs().toDate()),
  },
  'Last year': {
    from: toPickerFormat(
      dayjs().startOf('year').subtract(1, 'day').startOf('year').toDate()
    ),
    to: toPickerFormat(
      dayjs().startOf('year').subtract(1, 'day').endOf('year').toDate()
    ),
  },
};

export function toPickerFormat(date: string | Date) {
  return dayjs(date).format('YYYY/MM/DD');
}

export function relativeDateOptionsUpToOneMonth(
  formatter: (typeof dateTimeFormats)['en-US']
): Array<DateTimeRangeOption> {
  return [
    {
      id: 'today',
      label: 'Today',
      value: {
        from: dayjs().startOf('day').toISOString(),
        to: dayjs().endOf('day').toISOString(),
      },
      displayRange: `${dayjs().format(formatter.date)}`,
    },
    {
      id: 'yesterday',
      label: 'Yesterday',
      value: {
        from: dayjs().subtract(1, 'day').startOf('day').toISOString(),
        to: dayjs().subtract(1, 'day').endOf('day').toISOString(),
      },
      displayRange: `${dayjs().subtract(1, 'day').format(formatter.date)}`,
    },
    {
      id: 'this-week',
      label: 'This week',
      value: {
        from: dayjs().startOf('week').toISOString(),
        to: dayjs().endOf('week').toISOString(),
      },
      displayRange: `${dayjs()
        .startOf('week')
        .format(formatter.date)} - ${dayjs()
        .endOf('week')
        .format(formatter.date)}`,
    },
    {
      id: 'last-week',
      label: 'Last week',
      value: {
        from: dayjs().subtract(1, 'week').startOf('week').toISOString(),
        to: dayjs().subtract(1, 'week').endOf('week').toISOString(),
      },
      displayRange: `${dayjs()
        .subtract(1, 'week')
        .startOf('week')
        .format(formatter.date)} - ${dayjs()
        .subtract(1, 'week')
        .endOf('week')
        .format(formatter.date)}`,
    },
    {
      id: 'last-7-days',
      label: 'Last 7 days',
      value: {
        from: dayjs().subtract(6, 'day').startOf('day').toISOString(),
        to: dayjs().endOf('day').toISOString(),
      },
      displayRange: `${dayjs()
        .subtract(6, 'day')
        .startOf('day')
        .format(formatter.date)} - ${dayjs().format(formatter.date)}`,
    },
    {
      id: 'last-14-days',
      label: 'Last 14 days',
      value: {
        from: dayjs().subtract(13, 'day').startOf('day').toISOString(),
        to: dayjs().endOf('day').toISOString(),
      },
      displayRange: `${dayjs()
        .subtract(13, 'day')
        .startOf('day')
        .format(formatter.date)} - ${dayjs().format(formatter.date)}`,
    },
    {
      id: 'last-30-days',
      label: 'Last 30 days',
      value: {
        from: dayjs().subtract(29, 'day').startOf('day').toISOString(),
        to: dayjs().endOf('day').toISOString(),
      },
      displayRange: `${dayjs()
        .subtract(29, 'day')
        .startOf('day')
        .format(formatter.date)} - ${dayjs().format(formatter.date)}`,
    },
    {
      id: 'this-month',
      label: 'This month',
      value: {
        from: dayjs().startOf('month').toISOString(),
        to: dayjs().endOf('month').toISOString(),
      },
      displayRange: `${dayjs()
        .startOf('month')
        .format(formatter.date)} - ${dayjs()
        .endOf('month')
        .format(formatter.date)}`,
    },
    {
      id: 'last-month',
      label: 'Last month',
      value: {
        from: dayjs().subtract(1, 'month').startOf('month').toISOString(),
        to: dayjs().subtract(1, 'month').endOf('month').toISOString(),
      },
      displayRange: `${dayjs()
        .subtract(1, 'month')
        .startOf('month')
        .format(formatter.date)} - ${dayjs()
        .subtract(1, 'month')
        .endOf('month')
        .format(formatter.date)}`,
    },
    {
      id: 'this-month-to-date',
      label: 'This month to date',
      value: {
        from: dayjs().startOf('month').toISOString(),
        to: dayjs().endOf('day').toISOString(),
      },
      displayRange: `${dayjs()
        .startOf('month')
        .format(formatter.date)} - ${dayjs().format(formatter.date)}`,
    },
    {
      id: 'last-year',
      label: 'Last year',
      value: {
        from: dayjs().subtract(1, 'year').startOf('year').toISOString(),
        to: dayjs().subtract(1, 'year').endOf('year').toISOString(),
      },
      displayRange: `${dayjs()
        .subtract(1, 'year')
        .startOf('year')
        .format(formatter.date)} - ${dayjs()
        .subtract(1, 'year')
        .endOf('year')
        .format(formatter.date)}`,
    },
    {
      id: 'this-year-to-date',
      label: 'This year to date',
      value: {
        from: dayjs().startOf('year').toISOString(),
        to: dayjs().endOf('day').toISOString(),
      },
      displayRange: `${dayjs()
        .startOf('year')
        .format(formatter.date)} - ${dayjs().format(formatter.date)}`,
    },
  ];
}

export function reportingRelativeDateOptions(
  formatter: (typeof dateTimeFormats)['en-US']
): Array<DateTimeRangeOption> {
  return [
    ...relativeDateOptionsUpToOneMonth(formatter).filter(
      (option) => option.label !== 'Today' && option.label !== 'Yesterday'
    ),
    {
      id: 'last-month',
      label: 'Last month',
      value: {
        from: dayjs().subtract(1, 'month').startOf('month').toISOString(),
        to: dayjs().subtract(1, 'month').endOf('month').toISOString(),
      },
      displayRange: `${dayjs()
        .subtract(1, 'month')
        .startOf('month')
        .format(formatter.date)} - ${dayjs()
        .subtract(1, 'month')
        .endOf('month')
        .format(formatter.date)}`,
    },
    {
      id: 'last-3-months',
      label: 'Last 3 months',
      value: {
        from: dayjs().subtract(3, 'month').startOf('month').toISOString(),
        to: dayjs().subtract(1, 'month').endOf('month').toISOString(),
      },
      displayRange: `${dayjs()
        .subtract(3, 'month')
        .startOf('month')
        .format(formatter.date)} - ${dayjs()
        .subtract(1, 'month')
        .endOf('month')
        .format(formatter.date)}`,
    },
    {
      id: 'this-quarter',
      label: 'This quarter',
      value: {
        from: dayjs().startOf('quarter').toISOString(),
        to: dayjs().endOf('quarter').toISOString(),
      },
      displayRange: `${dayjs()
        .startOf('quarter')
        .format(formatter.date)} - ${dayjs()
        .endOf('quarter')
        .format(formatter.date)}`,
    },
    {
      id: 'last-quarter',
      label: 'Last quarter',
      value: {
        from: dayjs().subtract(1, 'quarter').startOf('quarter').toISOString(),
        to: dayjs().subtract(1, 'quarter').endOf('quarter').toISOString(),
      },
      displayRange: `${dayjs()
        .subtract(1, 'quarter')
        .startOf('quarter')
        .format(formatter.date)} - ${dayjs()
        .subtract(1, 'quarter')
        .endOf('quarter')
        .format(formatter.date)}`,
    },
    {
      id: 'this-year',
      label: 'This year',
      value: {
        from: dayjs().startOf('year').toISOString(),
        to: dayjs().endOf('year').toISOString(),
      },
      displayRange: `${dayjs()
        .startOf('year')
        .format(formatter.date)} - ${dayjs()
        .endOf('year')
        .format(formatter.date)}`,
    },
    {
      id: 'last-year',
      label: 'Last year',
      value: {
        from: dayjs().subtract(1, 'year').startOf('year').toISOString(),
        to: dayjs().subtract(1, 'year').endOf('year').toISOString(),
      },
      displayRange: `${dayjs()
        .subtract(1, 'year')
        .startOf('year')
        .format(formatter.date)} - ${dayjs()
        .subtract(1, 'year')
        .endOf('year')
        .format(formatter.date)}`,
    },
  ];
}

export function formatLabel(
  dateString: string,
  includeTime = false,
  timezone?: string
) {
  if (!dateString) return '';
  const inputDate = timezone
    ? dayjs(dateString).tz(timezone)
    : dayjs(dateString);
  const now = timezone ? dayjs().tz(timezone) : dayjs();
  const currentYear = now.year();
  const today = now.startOf('day');
  const tomorrow = today.add(1, 'day');
  const yesterday = today.subtract(1, 'day');
  const oneWeekLater = today.add(7, 'day');

  const timeFormat = includeTime ? ' h:mm A' : '';

  if (
    !includeTime &&
    inputDate.isSameOrAfter(yesterday) &&
    inputDate.isBefore(oneWeekLater)
  ) {
    if (inputDate.isSame(today, 'day')) {
      return 'Today';
    }
    if (inputDate.isSame(tomorrow, 'day')) {
      return 'Tomorrow';
    }
    if (inputDate.isSame(yesterday, 'day')) {
      return 'Yesterday';
    }
    return inputDate.format('dddd');
  } else {
    if (inputDate.year() !== currentYear) {
      return inputDate.format(`MMM D, YYYY${timeFormat}`);
    }
    return inputDate.format(`MMM D${timeFormat}`);
  }
}
